import { useState, useEffect } from "react";
import styled from "styled-components";
import { Card, Input, Label, Wrapper } from "../CommonComponents/DataCard";
import PasswordChangeService from "../../services/PasswordChangeService";
import Button from "../CommonComponents/Button";
import { useNavigate } from "react-router-dom";

// ── Step indicator ────────────────────────────────────────────────

const StepRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2rem;
    width: 100%;
`;

const StepItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.2rem;
`;

const StepCircle = styled.div`
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
    font-weight: 700;
    flex-shrink: 0;
    background: ${({ $active, $done, theme }) =>
        ($active || $done) ? theme.colors.primary : theme.colors.buttonBackground};
    color: ${({ $active, $done, theme }) =>
        ($active || $done) ? "#fff" : theme.colors.buttonText};
    transition: background 0.3s, color 0.3s;
`;

const StepLabel = styled.span`
    font-size: 0.75rem;
    font-weight: 600;
    color: ${({ $active, $done, theme }) =>
        ($active || $done) ? theme.colors.primary : theme.colors.lineColor};
    transition: color 0.3s;
`;

const StepLine = styled.div`
    flex: 1;
    height: 2px;
    margin-bottom: 1.2rem;
    min-width: 2.5rem;
    background: ${({ $done, theme }) =>
        $done ? theme.colors.primary : theme.colors.buttonBackground};
    transition: background 0.3s;
`;

// ── Ostali stilovi ────────────────────────────────────────────────

const FormTitle = styled.h2`
    color: ${({ theme }) => theme.colors.primary};
    font-size: 1.5rem;
    margin: 0 0 0.25rem 0;
    text-align: center;
`;

const FormSubtitle = styled.p`
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.88rem;
    text-align: center;
    margin: 0 0 1.5rem 0;
    opacity: 0.7;
    line-height: 1.5;
`;

const EmailBadge = styled.div`
    background: ${({ theme }) => theme.colors.primary}18;
    color: ${({ theme }) => theme.colors.primary};
    border: 1px solid ${({ theme }) => theme.colors.primary}44;
    border-radius: 8px;
    padding: 0.4rem 0.9rem;
    font-size: 0.85rem;
    font-weight: 600;
    margin-bottom: 1.25rem;
    text-align: center;
    word-break: break-all;
`;

const StatusMsg = styled.p`
    font-size: 0.85rem;
    font-weight: 600;
    text-align: center;
    margin: 0.75rem 0 0 0;
    color: ${({ $error, theme }) => $error ? theme.status.error : theme.status.success};
`;

const SuccessBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0 1rem 0;
    animation: fadeInUp 0.4s ease;

    @keyframes fadeInUp {
        from { opacity: 0; transform: translateY(8px); }
        to   { opacity: 1; transform: translateY(0); }
    }
`;

const BigCheck = styled.div`
    font-size: 3rem;
    line-height: 1;
`;

const CountdownText = styled.p`
    font-size: 0.82rem;
    color: ${({ theme }) => theme.colors.text};
    opacity: 0.55;
    margin: 0;
`;

// ── Step indikator ────────────────────────────────────────────────

const STEP_LABELS = ["Email", "Nova lozinka"];

function StepIndicator({ step }) {
    return (
        <StepRow>
            {STEP_LABELS.map((label, i) => (
                <>
                    <StepItem key={i}>
                        <StepCircle $active={step === i + 1} $done={step > i + 1}>
                            {step > i + 1 ? "✓" : i + 1}
                        </StepCircle>
                        <StepLabel $active={step === i + 1} $done={step > i + 1}>
                            {label}
                        </StepLabel>
                    </StepItem>
                    {i < STEP_LABELS.length - 1 && (
                        <StepLine key={`line-${i}`} $done={step > i + 1} />
                    )}
                </>
            ))}
        </StepRow>
    );
}

// ── Glavna komponenta ─────────────────────────────────────────────

function PasswordChangeRequest() {
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [message, setMessage] = useState(null);
    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [countdown, setCountdown] = useState(3);

    const navigate = useNavigate();

    useEffect(() => {
        if (!done) return;
        if (countdown === 0) { navigate("/"); return; }
        const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [done, countdown, navigate]);

    const showError = (msg) => { setMessage(msg); setIsError(true); };
    const showInfo  = (msg) => { setMessage(msg); setIsError(false); };

    const handleSendCode = async () => {
        if (!email.trim()) { showError("Unesite email adresu"); return; }
        setLoading(true); setMessage(null);
        try {
            await PasswordChangeService.requestReset(email.trim());
            showInfo("Kod je poslan na vaš email");
            setStep(2);
        } catch (err) {
            showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async () => {
        if (!code || !newPassword || !confirmPassword) {
            showError("Popunite sva polja"); return;
        }
        if (newPassword !== confirmPassword) {
            showError("Lozinke se ne podudaraju"); return;
        }
        setLoading(true); setMessage(null);
        try {
            await PasswordChangeService.resetPassword(email, code, newPassword);
            setDone(true);
        } catch (err) {
            showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Wrapper>
            <Card style={{ width: "360px" }}>
                <StepIndicator step={done ? 3 : step} />

                {!done ? (
                    <>
                        {step === 1 && (
                            <>
                                <FormTitle>Zaboravljena lozinka</FormTitle>
                                <FormSubtitle>
                                    Unesite email koji ste koristili pri registraciji.
                                    Poslat ćemo vam kod za promjenu lozinke.
                                </FormSubtitle>
                                <Label>Email adresa</Label>
                                <Input
                                    type="email"
                                    placeholder="ime@primjer.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && handleSendCode()}
                                    autoFocus
                                />
                                <Button
                                    tekst={loading ? "Slanje..." : "Pošalji kod"}
                                    akcija={handleSendCode}
                                    disabled={loading}
                                    sirina="100%"
                                />
                            </>
                        )}

                        {step === 2 && (
                            <>
                                <FormTitle>Nova lozinka</FormTitle>
                                <FormSubtitle>
                                    Unesite kod koji smo poslali na:
                                </FormSubtitle>
                                <EmailBadge>📧 {email}</EmailBadge>

                                <Label>Kod iz emaila</Label>
                                <Input
                                    placeholder="Unesite kod"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    autoFocus
                                />

                                <Label>Nova lozinka</Label>
                                <Input
                                    type="password"
                                    placeholder="Nova lozinka"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />

                                <Label>Potvrdi lozinku</Label>
                                <Input
                                    type="password"
                                    placeholder="Ponovi lozinku"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && handleChangePassword()}
                                />

                                <Button
                                    tekst={loading ? "Mijenjanje..." : "Promijeni lozinku"}
                                    akcija={handleChangePassword}
                                    disabled={loading}
                                    sirina="100%"
                                />
                            </>
                        )}

                        {message && <StatusMsg $error={isError}>{message}</StatusMsg>}
                    </>
                ) : (
                    <SuccessBox>
                        <BigCheck>✅</BigCheck>
                        <FormTitle>Lozinka promijenjena!</FormTitle>
                        <FormSubtitle style={{ marginBottom: 0 }}>
                            Vaša lozinka je uspješno promijenjena.
                        </FormSubtitle>
                        <CountdownText>Preusmjeravanje za {countdown}...</CountdownText>
                    </SuccessBox>
                )}
            </Card>
        </Wrapper>
    );
}

export default PasswordChangeRequest;
